import React, { useState } from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const OperationalMetricsTab = () => {
  const [selectedMetric, setSelectedMetric] = useState('productivity');

  const productivityData = [
    { month: 'Jan', productivity: 85, efficiency: 78, quality: 92 },
    { month: 'Feb', productivity: 88, efficiency: 82, quality: 89 },
    { month: 'Mar', productivity: 92, efficiency: 85, quality: 94 },
    { month: 'Apr', productivity: 87, efficiency: 80, quality: 91 },
    { month: 'May', productivity: 90, efficiency: 88, quality: 96 },
    { month: 'Jun', productivity: 94, efficiency: 91, quality: 93 },
    { month: 'Jul', productivity: 89, efficiency: 86, quality: 95 },
    { month: 'Aug', productivity: 93, efficiency: 89, quality: 97 },
    { month: 'Sep', productivity: 91, efficiency: 87, quality: 94 },
    { month: 'Oct', productivity: 96, efficiency: 92, quality: 98 }
  ];

  const projectStatusData = [
    { name: 'Completed', value: 45, color: '#059669' },
    { name: 'In Progress', value: 32, color: '#2563EB' },
    { name: 'On Hold', value: 15, color: '#D97706' },
    { name: 'Cancelled', value: 8, color: '#DC2626' }
  ];

  const departmentMetrics = [
    {
      department: 'Engineering',
      projects: 12,
      completion: 87,
      budget: 95000,
      utilization: 92
    },
    {
      department: 'Design',
      projects: 8,
      completion: 94,
      budget: 45000,
      utilization: 78
    },
    {
      department: 'Product',
      projects: 6,
      completion: 91,
      budget: 35000,
      utilization: 85
    },
    {
      department: 'QA',
      projects: 10,
      completion: 89,
      budget: 28000,
      utilization: 88
    }
  ];

  const kpiMetrics = [
    {
      id: 1,
      title: "Average Project Duration",
      value: "45 days",
      change: "-8 days",
      trend: "down",
      icon: "Clock",
      description: "Compared to last quarter"
    },
    {
      id: 2,
      title: "Team Satisfaction Score",
      value: "4.2/5.0",
      change: "+0.3",
      trend: "up",
      icon: "Heart",
      description: "Based on monthly surveys"
    },
    {
      id: 3,
      title: "Resource Allocation",
      value: "89%",
      change: "+5%",
      trend: "up",
      icon: "Users",
      description: "Optimal resource usage"
    },
    {
      id: 4,
      title: "Delivery Success Rate",
      value: "94%",
      change: "+2%",
      trend: "up",
      icon: "Target",
      description: "On-time project delivery"
    }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(amount);
  };

  return (
    <div className="space-y-6">
      {/* KPI Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpiMetrics?.map((metric) => (
          <div key={metric?.id} className="bg-card border border-border rounded-lg p-6 card-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 rounded-lg bg-primary/10">
                <Icon name={metric?.icon} size={20} className="text-primary" />
              </div>
              <div className={`flex items-center text-sm ${
                metric?.trend === 'up' ? 'text-success' : 'text-warning'
              }`}>
                <Icon 
                  name={metric?.trend === 'up' ? 'TrendingUp' : 'TrendingDown'} 
                  size={16} 
                  className="mr-1" 
                />
                {metric?.change}
              </div>
            </div>
            <div className="space-y-1">
              <h3 className="text-2xl font-semibold text-foreground">{metric?.value}</h3>
              <p className="text-sm font-medium text-foreground">{metric?.title}</p>
              <p className="text-xs text-muted-foreground">{metric?.description}</p>
            </div>
          </div>
        ))}
      </div>
      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Productivity Trends */}
        <div className="bg-card border border-border rounded-lg p-6 card-shadow">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-foreground">Productivity Trends</h3>
            <select
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e?.target?.value)}
              className="px-3 py-1 border border-border rounded-md text-sm bg-input text-foreground"
            >
              <option value="productivity">Productivity</option>
              <option value="efficiency">Efficiency</option>
              <option value="quality">Quality</option>
            </select>
          </div>
          <div className="w-full h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={productivityData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" stroke="#64748B" fontSize={12} />
                <YAxis stroke="#64748B" fontSize={12} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#FFFFFF', 
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px'
                  }} 
                />
                <Line 
                  type="monotone" 
                  dataKey={selectedMetric} 
                  stroke="#2563EB" 
                  strokeWidth={2}
                  dot={{ fill: '#2563EB', strokeWidth: 2, r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Project Status Distribution */}
        <div className="bg-card border border-border rounded-lg p-6 card-shadow">
          <h3 className="text-lg font-semibold text-foreground mb-6">Project Status Distribution</h3>
          <div className="w-full h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={projectStatusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {projectStatusData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry?.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#FFFFFF', 
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px'
                  }} 
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4">
            {projectStatusData?.map((item, index) => (
              <div key={index} className="flex items-center space-x-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: item?.color }}
                />
                <span className="text-sm text-muted-foreground">{item?.name}</span>
                <span className="text-sm font-medium text-foreground">{item?.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Department Performance Table */}
      <div className="bg-card border border-border rounded-lg p-6 card-shadow">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground">Department Performance</h3>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
              Export
            </Button>
            <Button variant="outline" size="sm" iconName="RefreshCw" iconPosition="left">
              Refresh
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 font-medium text-muted-foreground">Department</th>
                <th className="text-left py-3 px-4 font-medium text-muted-foreground">Active Projects</th>
                <th className="text-left py-3 px-4 font-medium text-muted-foreground">Completion Rate</th>
                <th className="text-left py-3 px-4 font-medium text-muted-foreground">Budget Allocated</th>
                <th className="text-left py-3 px-4 font-medium text-muted-foreground">Utilization</th>
                <th className="text-left py-3 px-4 font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {departmentMetrics?.map((dept, index) => (
                <tr key={index} className="border-b border-border last:border-b-0">
                  <td className="py-4 px-4">
                    <div className="font-medium text-foreground">{dept?.department}</div>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-foreground">{dept?.projects}</span>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-foreground">{dept?.completion}%</span>
                      <div className="w-16 bg-muted rounded-full h-2">
                        <div
                          className="bg-success h-2 rounded-full"
                          style={{ width: `${dept?.completion}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-foreground">{formatCurrency(dept?.budget)}</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className={`font-medium ${
                      dept?.utilization >= 90 ? 'text-success' :
                      dept?.utilization >= 75 ? 'text-warning' : 'text-error'
                    }`}>
                      {dept?.utilization}%
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <Button variant="ghost" size="sm" iconName="MoreHorizontal" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OperationalMetricsTab;